import {
  Field,
  FieldError,
  FieldInput,
  FieldLabel,
} from "@strapi/design-system";
import { Grid, GridItem } from "@strapi/design-system/Grid";
import { DatePicker, Radio, Select } from "antd";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import { dateFormatList } from "../../util/helper";
import DegreeSelect from "../degreeSelect";

const NomineeInfoTab = ({
  dataForUpdate,
  setDataForUpdate,
  isSecretariatView = false,
}) => {
  const { t } = useTranslation();
  const countries = useMemo(() => countryList().getData(), []);

  //#region Handle update info
  const handleSetDob = (date, dateString) => {
    if (dateString === "") {
      setDataForUpdate({
        ...dataForUpdate,
        dob: null,
      });
    } else {
      setDataForUpdate({
        ...dataForUpdate,
        dob: dayjs(date).format(dateFormatList[1]),
      });
    }
  };

  const handleSetName = (name) => {
    setDataForUpdate({
      ...dataForUpdate,
      name,
    });
  };

  const handleSetAddress = (address) => {
    setDataForUpdate({
      ...dataForUpdate,
      address,
    });
  };

  const handleSetPhone = (phone) => {
    const formattedPhoneNumber = phone.replace(/[^0-9+\-().\s]/g, "");

    setDataForUpdate({
      ...dataForUpdate,
      phone: formattedPhoneNumber,
    });
  };

  const handleSetEmail = (email) => {
    setDataForUpdate({
      ...dataForUpdate,
      email,
    });
  };

  const handleSetDegree = (degree) => {
    setDataForUpdate({
      ...dataForUpdate,
      degree,
    });
  };

  const handleSetGender = (gender) => {
    setDataForUpdate({
      ...dataForUpdate,
      gender,
    });
  };

  const handleSetPlaceOfBirth = (place_of_birth) => {
    setDataForUpdate({
      ...dataForUpdate,
      place_of_birth,
    });
  };

  const handleSetNationality = (nationality) => {
    setDataForUpdate({
      ...dataForUpdate,
      nationality,
    });
  };

  const handleSetPosition = (position) => {
    setDataForUpdate({
      ...dataForUpdate,
      position,
    });
  };

  const handleSetAddressWorkingUnit = (address_working_unit) => {
    setDataForUpdate({
      ...dataForUpdate,
      address_working_unit,
    });
  };

  const handleSetWebWorkingUnit = (web_working_unit) => {
    setDataForUpdate({
      ...dataForUpdate,
      web_working_unit,
    });
  };

  //#endregion

  return (
    <Grid gap={5}>
      <GridItem col={3} s={6} xs={12}>
        <Field
          name="name"
          error={
            !dataForUpdate?.name || dataForUpdate?.name === ""
              ? t("str_required_full_name")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_nominee_name")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.name}
            onChange={(e) => handleSetName(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="dob"
          error={
            !dataForUpdate?.dob ||
            dataForUpdate?.dob === "" ||
            dataForUpdate?.dob === null
              ? t("str_required_dob")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_dob")}
          </FieldLabel>
          <DatePicker
            value={dataForUpdate?.dob ? dayjs(dataForUpdate?.dob) : null}
            format={dateFormatList[0]}
            placeholder="dd/mm/yyyy"
            className="custom-date-picker"
            onChange={handleSetDob}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="gender"
          error={
            !dataForUpdate?.gender || dataForUpdate?.gender === ""
              ? t("str_required_gender")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_gender")}
          </FieldLabel>
          <Radio.Group
            value={dataForUpdate?.gender}
            onChange={(e) => handleSetGender(e.target.value)}
            style={{ marginTop: "10px" }}
            disabled={isSecretariatView}
          >
            <Radio value="male">{t("str_male")}</Radio>
            <Radio value="female">{t("str_female")}</Radio>
          </Radio.Group>

          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="place_of_birth"
          error={
            !dataForUpdate?.place_of_birth ||
            dataForUpdate?.place_of_birth === "" ||
            dataForUpdate?.place_of_birth === null
              ? t("str_required_place_of_birth")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_place_of_birth")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.place_of_birth}
            onChange={(e) => handleSetPlaceOfBirth(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="nationality"
          error={
            !dataForUpdate?.nationality ||
            dataForUpdate?.nationality === "" ||
            dataForUpdate?.nationality === null
              ? t("str_required_nationality")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_nationality")}
          </FieldLabel>
          <Select
            value={dataForUpdate?.nationality}
            onChange={handleSetNationality}
            allowClear
            options={countries}
            className="custom-select"
            showSearch
            disabled={isSecretariatView}
            filterOption={(inputValue, option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.value.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="address"
          error={
            !dataForUpdate?.address ||
            dataForUpdate?.address === "" ||
            dataForUpdate?.address === null
              ? t("str_required_address")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_nominee_address")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.address}
            onChange={(e) => handleSetAddress(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="phone"
          error={
            !dataForUpdate?.phone ||
            dataForUpdate?.phone === "" ||
            dataForUpdate?.phone === null
              ? t("str_required_phone_number")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_nominee_phone_number")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.phone}
            onChange={(e) => handleSetPhone(e.target.value)}
            disabled={isSecretariatView}
            pattern= "^\+?[0-9]{1,4}[-\s.]?(\(?[0-9]+\)?[-\s.]?){4,}$" // Set the pattern attribute for HTML5 validation
          />
          <FieldError />
        </Field>
      </GridItem>
      <GridItem col={3} s={6} xs={12}>
        <Field
          name="email"
          error={
            !dataForUpdate?.email ||
            dataForUpdate?.email === "" ||
            dataForUpdate?.email === null
              ? t("str_required_email")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_nominee_email")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.email}
            onChange={(e) => handleSetEmail(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>
      <GridItem col={3} s={6} xs={12}>
        <Field
          name="degree"
          error={
            !dataForUpdate?.degree ||
            dataForUpdate?.degree === "" ||
            dataForUpdate?.degree === null
              ? t("str_required_academic_rank_and_degree")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_nominator_academic_rank_degree")}
          </FieldLabel>
          <DegreeSelect
            lang={t("str_lang")}
            value={dataForUpdate?.degree}
            onChange={handleSetDegree}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="position"
          error={
            !dataForUpdate?.position ||
            dataForUpdate?.position === "" ||
            dataForUpdate?.position === null
              ? t("str_required_position")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_current_position_and_workplace")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.position}
            onChange={(e) => handleSetPosition(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="address_working_unit"
          error={
            !dataForUpdate?.address_working_unit ||
            dataForUpdate?.address_working_unit === "" ||
            dataForUpdate?.address_working_unit === null
              ? t("str_required_working_place_address")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_work_unit_address")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.address_working_unit}
            onChange={(e) => handleSetAddressWorkingUnit(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>

      <GridItem col={3} s={6} xs={12}>
        <Field
          name="web_working_unit"
          error={
            !dataForUpdate?.web_working_unit ||
            dataForUpdate?.web_working_unit === "" ||
            dataForUpdate?.web_working_unit === null
              ? t("str_required_working_place_website")
              : ""
          }
        >
          <FieldLabel required style={{ marginBottom: "5px" }}>
            {t("str_work_unit_website")}
          </FieldLabel>
          <FieldInput
            value={dataForUpdate?.web_working_unit}
            onChange={(e) => handleSetWebWorkingUnit(e.target.value)}
            disabled={isSecretariatView}
          />
          <FieldError />
        </Field>
      </GridItem>
    </Grid>
  );
};

export default NomineeInfoTab;
